import { InfoCircleOutlined } from '@ant-design/icons';
import { Col, Row, Space, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SingleUploadField from '../../../components/fields/SingleUploadField';
import TranslatedTextAreaField from '../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import { useThemeComponents } from '../../../themes';
import { allowedExtensions } from '../../../utilities/extensions';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import { colSpan } from '../WebpageDetailsPage/WebpageBlock/ColumnListBlock';
import MaintenanceToolTip from './MaintenanceToolTip';

const StyledContainer = styled(Row)`
    background-color: #fff;
    padding: 20px;
`;

const StyledSpan = styled.span`
    padding-right: 5px;
`;

type MaintenanceDetailsFormProps = {
    updated: string;
    offset: string;
};
const MaintenanceDetailsForm = ({ updated, offset }: MaintenanceDetailsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'maintenance']);
    const { FormFields } = useThemeComponents();

    const { yesNoSwitch } = useSystemSwitchData();
    const labelWithTooltip = useMemo(
        () => (
            <span>
                <StyledSpan>{t('maintenance:fields.warningBefore.label')}</StyledSpan>
                <Tooltip placement="bottom" title={MaintenanceToolTip}>
                    <InfoCircleOutlined />
                </Tooltip>
            </span>
        ),
        [t]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('maintenance:panelTitles.mainDetails')}>
                <Panel key={t('maintenance:panelTitles.mainDetails')} header={t('maintenance:panelTitles.mainDetails')}>
                    <StyledContainer gutter={10}>
                        <Col {...colSpan}>
                            <FormFields.RangePickerField
                                {...t('maintenance:fields.period', { returnObjects: true })}
                                name="period"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TimePickerField
                                {...t('maintenance:fields.startTime', { returnObjects: true })}
                                name="startTime"
                                offset={offset}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TimePickerField
                                {...t('maintenance:fields.endTime', { returnObjects: true })}
                                name="endTime"
                                offset={offset}
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputField label={labelWithTooltip} name="warningBefore" required />
                        </Col>

                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('maintenance:fields.pageTitle', { returnObjects: true })}
                                name="pageTitle"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t(`maintenance:fields.pageContent`, { returnObjects: true })}
                                name="pageContent"
                            />
                        </Col>

                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('maintenance:fields.active', { returnObjects: true })}
                                name="isActive"
                                {...yesNoSwitch}
                            />
                        </Col>

                        <Col {...colSpan}>
                            <SingleUploadField
                                {...t('maintenance:fields.image', { returnObjects: true })}
                                extensions={allowedExtensions.image}
                                name="image"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('maintenance:fields.updatedAt', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    </StyledContainer>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default MaintenanceDetailsForm;
